// Regex-pattern parser used by the OCR text detector. The user's raw
// pattern string comes from the scan-screen input (persisted in
// localStorage as `petf:scan:text-pattern`); this module compiles it
// into a real RegExp and — when the pattern is digit-only — derives a
// Tesseract `tessedit_char_whitelist` so the OCR engine spends its
// budget on the right character set.

export interface ParsedTextPattern {
  regex: RegExp;
  /** Tesseract char whitelist when the pattern is digit-only. */
  whitelist?: string;
}

export const DEFAULT_TEXT_PATTERN = String.raw`^\d{3}$`;

/**
 * Compile a user-supplied pattern string to a RegExp + optional Tesseract
 * whitelist. Throws SyntaxError if the pattern doesn't compile — callers
 * are expected to catch and surface an error state.
 */
export function parseTextPattern(input: string): ParsedTextPattern {
  const trimmed = input.trim();
  const regex = new RegExp(trimmed);
  return isDigitOnlyPattern(trimmed) ? { regex, whitelist: '0123456789' } : { regex };
}

/**
 * Same as `parseTextPattern` but returns the default parsed pattern on
 * any error, so refs / long-lived callbacks never see an exception at
 * decode time.
 */
export function safeParseTextPattern(input: string): ParsedTextPattern {
  try {
    return parseTextPattern(input);
  } catch {
    return parseTextPattern(DEFAULT_TEXT_PATTERN);
  }
}

/**
 * Heuristic: pattern uses only digit character classes, digit literals,
 * anchors, and quantifiers. Deliberately narrow — false negatives (no
 * whitelist for a pattern that could have one) just make Tesseract
 * slower, not wrong. False positives would break user intent.
 */
function isDigitOnlyPattern(p: string): boolean {
  if (p.length === 0) return false;
  return /^[\^$\\d0-9{},*+?]+$/.test(p);
}
